import React, { useState, useMemo } from 'react';
import './TransactionsList.css';

function TransactionsList({ transactions }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAccount, setFilterAccount] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterTimeframe, setFilterTimeframe] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const parseAmount = (amountStr) => {
    if (!amountStr) return 0;
    const cleaned = amountStr.replace(/[$,]/g, '');
    return parseFloat(cleaned) || 0;
  };

  // Get unique accounts for filter
  const accounts = useMemo(() => {
    const accountSet = new Set();
    transactions.forEach(t => {
      if (t.Account) accountSet.add(t.Account);
    });
    return Array.from(accountSet).sort();
  }, [transactions]);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const categorySet = new Set();
    transactions.forEach(t => {
      const cat = t.SmartCategory || t.Category || 'Uncategorized';
      categorySet.add(cat);
    });
    return Array.from(categorySet).sort();
  }, [transactions]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter(transaction => {
      const matchesSearch =
        !searchTerm ||
        transaction.Description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.Account?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesAccount =
        filterAccount === 'all' ||
        transaction.Account === filterAccount;

      const transactionCategory = transaction.SmartCategory || transaction.Category || 'Uncategorized';
      const matchesCategory =
        filterCategory === 'all' ||
        transactionCategory === filterCategory;

      // Date filtering
      let matchesTimeframe = true;
      if (filterTimeframe !== 'all' && transaction.Date) {
        const days = parseInt(filterTimeframe);
        const now = new Date();
        const cutoff = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
        const parts = transaction.Date.split('/');
        if (parts.length >= 3) {
          const txDate = new Date(parts[2], parts[0] - 1, parts[1]);
          matchesTimeframe = txDate >= cutoff;
        }
      }

      return matchesSearch && matchesAccount && matchesCategory && matchesTimeframe;
    });

    // Sort transactions
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'date') {
        const dateA = a.Date ? new Date(a.Date.split('/')[2], a.Date.split('/')[0] - 1, a.Date.split('/')[1]) : new Date(0);
        const dateB = b.Date ? new Date(b.Date.split('/')[2], b.Date.split('/')[0] - 1, b.Date.split('/')[1]) : new Date(0);
        comparison = dateA - dateB;
      } else if (sortBy === 'amount') {
        comparison = parseAmount(a.Amount) - parseAmount(b.Amount);
      } else if (sortBy === 'description') {
        comparison = (a.Description || '').localeCompare(b.Description || '');
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [transactions, searchTerm, filterAccount, filterCategory, filterTimeframe, sortBy, sortOrder]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="transactions-list">
      <div className="transactions-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Accounts</option>
            {accounts.map(account => (
              <option key={account} value={account}>{account}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterTimeframe}
            onChange={(e) => setFilterTimeframe(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 6 months</option>
            <option value="365">Last year</option>
            <option value="730">Last 2 years</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="date">Sort by Date</option>
            <option value="amount">Sort by Amount</option>
            <option value="description">Sort by Description</option>
          </select>
        </div>

        <div className="filter-group">
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="sort-order-btn"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      <div className="transactions-count">
        Showing {filteredTransactions.length} of {transactions.length} transactions
      </div>

      <div className="transactions-table">
        <div className="transaction-header">
          <div>Date</div>
          <div>Description</div>
          <div>Category</div>
          <div>Account</div>
          <div>Amount</div>
        </div>

        {filteredTransactions.map((transaction, index) => {
          const amount = parseAmount(transaction.Amount);
          const isIncome = amount > 0;

          return (
            <div key={index} className="transaction-row">
              <div className="transaction-date">{transaction.Date || 'N/A'}</div>
              <div className="transaction-description">{transaction.Description || 'N/A'}</div>
              <div className="transaction-category">{transaction.SmartCategory || transaction.Category || 'Uncategorized'}</div>
              <div className="transaction-account">{transaction.Account || 'N/A'}</div>
              <div className={`transaction-amount-cell ${isIncome ? 'income' : 'expense'}`}>
                {isIncome ? '+' : ''}{formatCurrency(amount)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default TransactionsList;





